/**
 * Copy for a From sender the account can no longer send as (ENG-3916).
 *
 * Two reasons, each naming the sender. The same builder feeds both the inline
 * field error (where the sender object is on hand, so the name is real) and
 * the save-time toast (where only the error code is, so the name degrades).
 *
 * `useCase` no longer reaches the sentence — the copy stops at "Select a
 * different sender." — but it stays on the args and on `SENDER_ERROR_CODES` as
 * the classification of WHICH email the bad sender belongs to.
 *
 * Mailbox-not-connected keeps its own wording in `errorCopy.ts` — it is a
 * connection problem rather than a permission one.
 */

export type SenderErrorReason =
  | 'deactivated'
  | 'delegation'
  | 'mailboxNotConnected';

/** The email a sender belongs to. */
export const SENDER_USE_CASE = {
  candidateConfirmation: 'candidate confirmation email',
  interviewerConfirmation: 'interviewer confirmation email',
  slotSelected: 'slot selected email',
} as const;

export type SenderUseCase =
  (typeof SENDER_USE_CASE)[keyof typeof SENDER_USE_CASE];

/** Stands in when the sender's name can't be resolved (toast path). */
const UNNAMED_SENDER = 'This sender';

/** The one reason that is about the recruiter themselves, so it says "your". */
const MAILBOX_NOT_CONNECTED_MESSAGE =
  "Your mailbox isn't connected to Kula. Connect it in Settings, or select a different sender.";

export interface SenderErrorMessageArgs {
  reason: SenderErrorReason;
  /** Which email the sender belongs to. Classification only — the message no
   *  longer names it. */
  useCase: SenderUseCase;
  /** Sender's display name; omitted where only the error code is available. */
  userName?: string | null;
}

export function getSenderErrorMessage({
  reason,
  userName,
}: SenderErrorMessageArgs): string {
  // The scheduler's own mailbox: `filter_by_email_delegatable` always keeps the
  // requesting user, so their absence from it can only mean the mailbox isn't
  // connected — never delegation, which nobody has to grant themselves. Second
  // person, and it names the fix they can actually perform.
  if (reason === 'mailboxNotConnected') {
    return MAILBOX_NOT_CONNECTED_MESSAGE;
  }
  const subject = userName?.trim() || UNNAMED_SENDER;
  const cause =
    reason === 'deactivated'
      ? `${subject} is deactivated in Kula.`
      : `${subject} hasn't allowed sending emails on their behalf.`;
  return `${cause} Select a different sender.`;
}

/**
 * Backend codes → the reason + email they describe. Per-block by design: the
 * shared `err_email_delegation_disabled` it replaces couldn't say which email
 * the sender belonged to, which is what lets the caller attach the error to the
 * right field.
 */
export const SENDER_ERROR_CODES: Record<
  string,
  { reason: SenderErrorReason; useCase: SenderUseCase }
> = {
  err_candidate_email_sender_deactivated: {
    reason: 'deactivated',
    useCase: SENDER_USE_CASE.candidateConfirmation,
  },
  err_candidate_email_sender_delegation_disabled: {
    reason: 'delegation',
    useCase: SENDER_USE_CASE.candidateConfirmation,
  },
  err_interviewer_email_sender_deactivated: {
    reason: 'deactivated',
    useCase: SENDER_USE_CASE.interviewerConfirmation,
  },
  err_interviewer_email_sender_delegation_disabled: {
    reason: 'delegation',
    useCase: SENDER_USE_CASE.interviewerConfirmation,
  },
  // `candidate_mail_from` on AtsSchedule — the slot-selection email in the
  // self-schedule flow.
  err_candidate_mail_sender_deactivated: {
    reason: 'deactivated',
    useCase: SENDER_USE_CASE.slotSelected,
  },
  err_candidate_mail_sender_delegation_disabled: {
    reason: 'delegation',
    useCase: SENDER_USE_CASE.slotSelected,
  },
};
